export interface Rect {
  left: number;
  right: number;
  top: number;
  bottom: number;
  width: number;
  height: number;
}

export interface Pan {
  x: number;
  y: number;
}

/**
 * Clamp nextPan so the wrapper and container keep overlapping edges correctly.
 *
 * - Wrapper larger than container: wrapper must cover container
 *   (wrapper.left <= container.left and wrapper.right >= container.right)
 * - Wrapper smaller or equal: wrapper must stay inside container
 *   (wrapper.left >= container.left and wrapper.right <= container.right)
 *
 * Both cases reduce to the same pair of bounds on the displacement (dx, dy);
 * when wrapper <= container the bounds swap order, so we normalize lo/hi.
 *
 * @param currentPan  Pan value currently reflected in wrapperRect
 * @param nextPan     Desired new pan (before clamping)
 * @param containerRect  Scroll container bounding rect
 * @param wrapperRect    Wrapper bounding rect AT currentPan (post-transform)
 * @returns clamped pan
 */
export function clampPan(
  currentPan: Pan,
  nextPan: Pan,
  containerRect: Rect,
  wrapperRect: Rect,
): Pan {
  const clampAxis = (
    currentP: number,
    nextP: number,
    cStart: number,
    cEnd: number,
    wStart: number,
    wEnd: number,
  ): number => {
    const desired = nextP - currentP;
    const boundA = cEnd - wEnd;
    const boundB = cStart - wStart;
    const lo = Math.min(boundA, boundB);
    const hi = Math.max(boundA, boundB);
    const clamped = Math.max(lo, Math.min(hi, desired));
    return currentP + clamped;
  };

  const x = clampAxis(
    currentPan.x,
    nextPan.x,
    containerRect.left,
    containerRect.right,
    wrapperRect.left,
    wrapperRect.right,
  );
  const y = clampAxis(
    currentPan.y,
    nextPan.y,
    containerRect.top,
    containerRect.bottom,
    wrapperRect.top,
    wrapperRect.bottom,
  );

  return { x, y };
}
